import React, { useMemo, useState, useEffect } from 'react';
import { FALLBACK_IMAGE } from '../../utils/imageUtils';

// Extract Google Drive file id from various link formats
const extractDriveId = (url) => {
  if (!url) return null;
  try {
    if (!url.includes('drive.google.com')) return null;
    const byPath = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (byPath) return byPath[1];
    const byQuery = url.match(/[?&]id=([a-zA-Z0-9-_]+)/);
    if (byQuery) return byQuery[1];
    return null;
  } catch {
    return null;
  }
};

// Build a list of candidate URLs to try for an image
const buildCandidates = (src) => {
  if (!src) return [];
  const id = extractDriveId(src);
  if (!id) {
    return [src];
  }
  return [
    // View endpoint
    `https://drive.google.com/uc?export=view&id=${id}`,
    // Download endpoint (some images require download to render)
    `https://drive.google.com/uc?export=download&id=${id}`,
    // Thumbnail endpoint with size hint
    `https://drive.google.com/thumbnail?id=${id}&sz=w1200`,
    // Googleusercontent direct image service
    `https://lh3.googleusercontent.com/d/${id}=w1200`,
  ];
};

const SmartImage = ({ src, alt = '', style, className, width, height, onLoad, onError, imgStyle, ...rest }) => {
  const candidates = useMemo(() => buildCandidates(src), [src]);
  const [index, setIndex] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(candidates[0] || '');
  const [tried, setTried] = useState([]);

  useEffect(() => {
    setIndex(0);
    setCurrentSrc(candidates[0] || '');
    setTried([]);
    if (candidates.length > 0) {
      // Log candidates for debugging
      // eslint-disable-next-line no-console
      console.log('[SmartImage] Candidates for', src, candidates);
    }
  }, [candidates]);

  const handleError = (e) => {
    // eslint-disable-next-line no-console
    console.warn('[SmartImage] Failed to load', currentSrc);
    setTried((prev) => [...prev, currentSrc]);
    if (index < candidates.length - 1) {
      const next = index + 1;
      setIndex(next);
      setCurrentSrc(candidates[next]);
    } else {
      // eslint-disable-next-line no-console
      console.error('[SmartImage] All candidates failed for', src, 'tried:', tried);
      if (e && e.target) {
        e.target.src = FALLBACK_IMAGE;
      }
      if (onError) onError(e);
    }
  };

  const handleLoad = (e) => {
    // eslint-disable-next-line no-console
    console.log('[SmartImage] Loaded', currentSrc);
    if (onLoad) onLoad(e);
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      width={width}
      height={height}
      style={imgStyle || style}
      className={className}
      referrerPolicy="no-referrer"
      crossOrigin="anonymous"
      loading="lazy"
      onLoad={handleLoad}
      onError={handleError}
      {...rest}
    />
  );
};

export default SmartImage;


